<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { format } from 'date-fns';

import Table from 'dashboard/components/table/Table.vue';
import { useWhatsappErrorTranslation } from 'dashboard/composables/useWhatsappErrorTranslation';

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  hasError: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { translateError } = useWhatsappErrorTranslation();
const columnHelper = createColumnHelper();

const STATUS_BADGE = {
  pending: 'text-n-slate-12 bg-n-slate-3',
  sent: 'text-n-blue-11 bg-n-blue-3',
  delivered: 'text-n-teal-11 bg-n-teal-3',
  read: 'text-n-teal-11 bg-n-teal-3',
  replied: 'text-n-teal-11 bg-n-teal-3',
  failed: 'text-n-ruby-11 bg-n-ruby-3',
};

const renderContact = cellProps => {
  const row = cellProps.row.original;
  return h(
    'span',
    { class: 'text-n-slate-12' },
    row.contact_name || row.phone || '—'
  );
};

const renderStatus = cellProps => {
  const status = cellProps.getValue();
  return h(
    'span',
    {
      class: [
        'inline-flex items-center h-6 px-2 rounded-md text-xs font-medium',
        STATUS_BADGE[status] || STATUS_BADGE.pending,
      ],
    },
    t(`CAMPAIGN.ANALYTICS.STATUS.${status.toUpperCase()}`)
  );
};

const renderError = cellProps =>
  h('span', { class: 'text-n-ruby-11' }, translateError(cellProps.getValue()));

const renderSentAt = cellProps => {
  const value = cellProps.getValue();
  return h(
    'span',
    { class: 'text-n-slate-11' },
    value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '—'
  );
};

const columns = computed(() => {
  const definitions = [
    columnHelper.accessor('contact_name', {
      header: t('CAMPAIGN.ANALYTICS.TABLE.CONTACT'),
      cell: renderContact,
    }),
    columnHelper.accessor('status', {
      header: t('CAMPAIGN.ANALYTICS.TABLE.STATUS'),
      cell: renderStatus,
    }),
  ];

  if (props.hasError) {
    definitions.push(
      columnHelper.accessor('error_message', {
        header: t('CAMPAIGN.ANALYTICS.TABLE.ERROR_MESSAGE'),
        cell: renderError,
      })
    );
  }

  definitions.push(
    columnHelper.accessor('sent_at', {
      header: t('CAMPAIGN.ANALYTICS.TABLE.SENT_AT'),
      cell: renderSentAt,
    })
  );

  return definitions;
});

const table = useVueTable({
  get data() {
    return props.rows;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});
</script>

<template>
  <div
    class="overflow-auto rounded-md outline outline-1 outline-n-container bg-n-solid-2"
  >
    <Table v-if="rows.length" :table="table" />
    <div
      v-else
      class="flex flex-col items-center justify-center gap-1 py-12 text-center"
    >
      <p class="text-base font-medium text-n-slate-12">
        {{ t('CAMPAIGN.ANALYTICS.EMPTY_STATE.TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.ANALYTICS.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>
  </div>
</template>
